import React, { useEffect, useState, useCallback, useMemo } from "react";
import API from "../../utils/api";
import toast from "react-hot-toast";
import { downloadRowsAsXlsx } from "../../utils/excelDownload";

const GROUP_META = {
  support_services:       { label: "Support Services",       icon: "🛠️", color: "#3b82f6" },
  additional_expenses:    { label: "Additional Expenses",    icon: "⚡",  color: "#f59e0b" },
  administrative_charges: { label: "Administrative Charges", icon: "📋", color: "#8b5cf6" },
  food_expenses:          { label: "Food Expenses",          icon: "🍽️", color: "#10b981" },
};
const ROLE_COLOR = { "Venue Manager": "#6366f1", "Asst. Venue Manager": "#ec4899", "Other Staff": "#64748b" };
const CATEGORY_COLORS = { Travel:"#4f46e5", Hotel:"#0891b2", Food:"#059669", Fuel:"#d97706", Courier:"#7C3AED", Stationeries:"#E31E24", Prints:"#ec4899", Miscellaneous:"#6b7280" };

const fmt     = n => Number(n||0).toLocaleString("en-IN", { style:"currency", currency:"INR", maximumFractionDigits:0 });
const fmtDate = d => { try { return new Date(d+"T00:00:00").toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" }); } catch { return d; } };

const card    = { background:"#1e293b", borderRadius:16, padding:"20px 24px", marginBottom:20 };
const inp     = { width:"100%", padding:"10px 14px", borderRadius:10, border:"1px solid #334155", background:"#0f172a", color:"#f1f5f9", fontSize:14, boxSizing:"border-box", outline:"none" };
const lbl     = { display:"block", fontSize:12, fontWeight:600, color:"#94a3b8", marginBottom:6, textTransform:"uppercase", letterSpacing:"0.05em" };
const btn     = (bg, fg="#fff") => ({ background:bg, color:fg, border:"none", borderRadius:10, padding:"10px 18px", fontWeight:600, cursor:"pointer", fontSize:14 });
const tabBtn  = active => ({ padding:"10px 20px", border:"none", borderRadius:10, fontWeight:600, fontSize:14, cursor:"pointer", background:active?"#6366f1":"transparent", color:active?"#fff":"#94a3b8", transition:"all .2s" });
const statCard = color => ({ background:"#1e293b", borderRadius:14, padding:"18px 20px", borderLeft:`4px solid ${color}` });

export default function OETExamCosts() {
  const [tab, setTab]           = useState("sessions");
  const [centers, setCenters]   = useState([]);
  const [groups, setGroups]     = useState({});
  const [sessions, setSessions] = useState([]);
  const [session, setSession]   = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [managerExp, setManagerExp] = useState(null);
  const [crossCheck, setCrossCheck] = useState(null);  // cross-check results
  const [integrityMap, setIntegrityMap] = useState({}); // sessionId → severity
  const [reports, setReports]   = useState(null);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [companyCosts, setCompanyCosts] = useState([]);
  const [companyForm, setCompanyForm] = useState({ employee_id:"", trip_id:"", cost_type:"Hotel", expense_date:"", amount:"", hotel_name:"", hotel_location:"", travel_from:"", travel_to:"", booking_reference:"" });
  const [loading, setLoading]   = useState(false);

  const [newForm, setNewForm]       = useState({ exam_date:"", center_no:"", candidate_count:"", notes:"", invoice_ref:"" });
  const [showNewForm, setShowNewForm] = useState(false);
  const [rptFilter, setRptFilter]   = useState({ exam_date:"", center_no:"" });

  useEffect(() => {
    API.get("/oet-exams/meta").then(r => { setCenters(r.data.centers||[]); setGroups(r.data.expense_groups||{}); }).catch(() => toast.error("Failed to load center data"));
  }, []);

  const oetTestDates = useMemo(() => { const all = centers.flatMap(c => c.test_dates||[]); return [...new Set(all)].sort(); }, [centers]);

  // ── Auto-computed values from existing expenses (for food auto-fill) ─────────
  const adminCandidateCount = useMemo(() =>
    expenses.filter(e => e.expense_group==="administrative_charges" && ["per_candidate","consolidated_amount"].includes(e.expense_type))
            .reduce((s,e) => s + Number(e.quantity||0), 0), [expenses]);

  const totalSupportStaff = useMemo(() =>
    expenses.filter(e => e.expense_group==="support_services")
            .reduce((s,e) => s + Number(e.staff_count||0), 0), [expenses]);

  const invigilatorCount = useMemo(() =>
    expenses.filter(e => e.expense_group==="support_services" && e.expense_type==="invigilator")
            .reduce((s,e) => s + Number(e.staff_count||0), 0), [expenses]);

  // ── Sessions ──────────────────────────────────────────────────────────────
  const loadSessions = useCallback(() => {
    setLoading(true);
    API.get("/oet-exams/sessions").then(r => setSessions(r.data)).catch(() => toast.error("Failed to load")).finally(() => setLoading(false));
    // Load cross-check summary for integrity badges
    API.get("/oet-exams/cross-check-summary").then(r => {
      const map = {};
      (r.data.sessions || []).forEach(s => { map[s.session_id] = s.severity; });
      setIntegrityMap(map);
    }).catch(() => {});
  }, []);
  useEffect(() => { if (tab==="sessions") loadSessions(); }, [tab, loadSessions]);

  // ── Reports ───────────────────────────────────────────────────────────────
  const loadReports = () => {
    setLoading(true);
    const p = new URLSearchParams();
    if (rptFilter.exam_date) p.set("exam_date", rptFilter.exam_date);
    if (rptFilter.center_no) p.set("center_no", rptFilter.center_no);
    API.get(`/oet-exams/reports?${p}`).then(r => setReports(r.data)).catch(() => toast.error("Failed")).finally(() => setLoading(false));
  };
  useEffect(() => { if (tab==="reports") loadReports(); }, [tab]); // eslint-disable-line
  const loadCompanyCosts = useCallback(async () => {
    try {
      const [options, costs] = await Promise.all([API.get("/oet-exams/company-cost-options"), API.get("/oet-exams/company-costs")]);
      setCompanyOptions(options.data || []); setCompanyCosts(costs.data || []);
    } catch { toast.error("Failed to load company-paid costs"); }
  }, []);
  useEffect(() => { if (tab === "company") loadCompanyCosts(); }, [tab, loadCompanyCosts]);
  const saveCompanyCost = async (e) => {
    e.preventDefault();
    try {
      await API.post("/oet-exams/company-costs", companyForm);
      toast.success("Company-paid cost added");
      setCompanyForm(f => ({...f, amount:"", hotel_name:"", hotel_location:"", travel_from:"", travel_to:"", booking_reference:""}));
      loadCompanyCosts();
    } catch (err) { toast.error(err.response?.data?.error || "Failed to add company-paid cost"); }
  };

  // ── Open session ──────────────────────────────────────────────────────────
  const openSession = async (sess) => {
    setLoading(true);
    try {
      const r = await API.get(`/oet-exams/sessions/${sess.id}`);
      setSession(r.data.session); setExpenses(r.data.expenses); setManagerExp(null); setCrossCheck(null); setTab("entry");
      API.get(`/oet-exams/sessions/${sess.id}/manager-expenses`).then(r => setManagerExp(r.data)).catch(() => {});
      API.get(`/oet-exams/sessions/${sess.id}/cross-check`).then(r => setCrossCheck(r.data)).catch(() => {});
    } catch { toast.error("Failed to load session"); }
    finally { setLoading(false); }
  };

  const createSession = async (e) => {
    e.preventDefault();
    if (!newForm.exam_date || !newForm.center_no) { toast.error("Select exam date and center"); return; }
    try {
      const r = await API.post("/oet-exams/sessions", newForm);
      toast.success("Session created!"); setShowNewForm(false);
      setNewForm({ exam_date:"", center_no:"", candidate_count:"", notes:"", invoice_ref:"" });
      openSession(r.data);
    } catch (err) {
      const msg = err.response?.data?.error || "Failed";
      if (err.response?.status===409 && err.response?.data?.existing_id) { toast.error(msg); openSession({ id: err.response.data.existing_id }); }
      else toast.error(msg);
    }
  };

  const updateSession = async () => {
    try { await API.put(`/oet-exams/sessions/${session.id}`, { candidate_count:session.candidate_count, notes:session.notes, invoice_ref:session.invoice_ref }); toast.success("Saved"); }
    catch { toast.error("Failed to save"); }
  };

  const deleteSession = async (id) => {
    if (!window.confirm("Delete this session and all its expenses?")) return;
    try { await API.delete(`/oet-exams/sessions/${id}`); toast.success("Deleted"); loadSessions(); }
    catch { toast.error("Failed to delete"); }
  };

  const addExpenseRow = async (groupKey, typeKey, typeLabel) => {
    try { const r = await API.post(`/oet-exams/sessions/${session.id}/expenses`, { expense_group:groupKey, expense_type:typeKey, expense_label:typeLabel, amount:0 }); setExpenses(prev => [...prev, r.data]); }
    catch { toast.error("Failed to add row"); }
  };

  const updateExpense = (expId, field, value) => setExpenses(prev => prev.map(e => e.id===expId ? {...e, [field]:value} : e));

  const saveExpense = async (exp) => {
    try {
      await API.put(`/oet-exams/expenses/${exp.id}`, exp);
      toast.success("Saved", { duration:900 });
      // Refresh cross-check after any expense change
      if (session) API.get(`/oet-exams/sessions/${session.id}/cross-check`).then(r => setCrossCheck(r.data)).catch(() => {});
    }
    catch { toast.error("Failed to save"); }
  };

  const deleteExpense = async (expId) => {
    try { await API.delete(`/oet-exams/expenses/${expId}`); setExpenses(prev => prev.filter(e => e.id!==expId)); }
    catch { toast.error("Failed to delete"); }
  };

  const groupTotal = gk => expenses.filter(e => e.expense_group===gk).reduce((s,e) => s+Number(e.amount||0), 0);
  const sessionTotal = () => expenses.reduce((s,e) => s+Number(e.amount||0), 0);
  const costPerCandidate = () => { const c=Number(session?.candidate_count||0); return c>0 ? sessionTotal()/c : 0; };

  const exportExcel = () => {
    if (!session) return;
    const rows = [
      ["OET Exam Cost Report"],
      [`Exam Date: ${fmtDate(session.exam_date)}`,`Center: ${session.center_name} (${session.center_no})`,`Location: ${session.center_location}`],
      [`Venue Manager: ${session.venue_manager||"-"}`,`Candidates: ${session.candidate_count||0}`],[],
      ["Group","Type","Staff/Qty Count","Food Qty Ordered","Amount","Vendor","Payment Mode","Invoice","Paid Date","Remarks"],
      ...expenses.map(e => [GROUP_META[e.expense_group]?.label||e.expense_group, e.expense_label||e.expense_type, e.staff_count||e.quantity||"", e.quantity||"", e.amount||0, e.vendor_paid_to||"", e.payment_mode||"", e.invoice_number||"", e.paid_date||"", e.remarks||""]),
      [],[`OET Total: ${sessionTotal()}`],
      ...(managerExp?.by_person?.length ? [[],["-- Manager Expenses --"],["Name","Role","Total"],...managerExp.by_person.map(p=>[p.employee_name,p.role_label,p.total_amount]),[],["","Manager Total:",managerExp.total||0]] : []),
    ];
    const headerIndex = rows.findIndex(row => row[0] === "Group");
    downloadRowsAsXlsx({
      filename: `OET_${session.center_no}_${session.exam_date}.xlsx`,
      sheetName: "OET Exam Costs",
      title: rows[0][0],
      subtitleRows: rows.slice(1, headerIndex).flat().filter(Boolean),
      headers: rows[headerIndex],
      rows: rows.slice(headerIndex + 1),
    });
  };

  // ══════════════════════════════════════════════════════════════════════════
  return (
    <div style={{ maxWidth:1200, margin:"0 auto", padding:"24px 16px", color:"#f1f5f9" }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:12, marginBottom:24 }}>
        <div>
          <h1 style={{ margin:0, fontSize:26, fontWeight:700 }}>🏫 OET Exam Center Costing</h1>
          <p style={{ margin:"4px 0 0", color:"#94a3b8", fontSize:14 }}>Track all company-paid expenses per exam date and center</p>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <button style={tabBtn(tab==="sessions")} onClick={() => setTab("sessions")}>📋 Sessions</button>
          {session && <button style={tabBtn(tab==="entry")} onClick={() => setTab("entry")}>✏️ Expense Entry</button>}
          <button style={tabBtn(tab==="company")} onClick={() => setTab("company")}>Company-Paid Trip Costs</button>
          <button style={tabBtn(tab==="reports")} onClick={() => setTab("reports")}>📊 Reports</button>
        </div>
      </div>

      {/* ═══════════════════════ SESSIONS TAB ═══════════════════════ */}
      {tab==="sessions" && (
        <div>
          <div style={{ marginBottom:20 }}>
            <button style={btn("#6366f1")} onClick={() => setShowNewForm(v => !v)}>{showNewForm ? "✕ Cancel" : "+ New OET Session"}</button>
          </div>

          {showNewForm && (
            <div style={{ ...card, borderLeft:"4px solid #6366f1" }}>
              <h3 style={{ margin:"0 0 18px", fontSize:18 }}>Create OET Exam Session</h3>
              <form onSubmit={createSession}>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(200px, 1fr))", gap:16 }}>
                  <div>
                    <span style={lbl}>Exam Date *</span>
                    <select style={inp} required value={newForm.exam_date} onChange={e => setNewForm(f=>({...f,exam_date:e.target.value}))}>
                      <option value="">Select exam date…</option>
                      {oetTestDates.map(d => <option key={d} value={d}>{fmtDate(d)}</option>)}
                    </select>
                  </div>
                  <div>
                    <span style={lbl}>Center *</span>
                    <select style={inp} required value={newForm.center_no} onChange={e => setNewForm(f=>({...f,center_no:e.target.value}))}>
                      <option value="">Select center…</option>
                      {centers.map(c => <option key={c.center_no} value={c.center_no}>{c.center_no} — {c.center_name} ({c.location})</option>)}
                    </select>
                  </div>
                  <div>
                    <span style={lbl}>Candidate Count</span>
                    <input type="number" min="0" style={inp} placeholder="0" value={newForm.candidate_count} onChange={e => setNewForm(f=>({...f,candidate_count:e.target.value}))} />
                  </div>
                  <div>
                    <span style={lbl}>Invoice / Ref No.</span>
                    <input type="text" style={inp} placeholder="INV-001" value={newForm.invoice_ref} onChange={e => setNewForm(f=>({...f,invoice_ref:e.target.value}))} />
                  </div>
                </div>
                <div style={{ marginTop:14 }}>
                  <span style={lbl}>Notes</span>
                  <textarea style={{ ...inp, minHeight:60, resize:"none" }} placeholder="Any remarks…" value={newForm.notes} onChange={e => setNewForm(f=>({...f,notes:e.target.value}))} />
                </div>
                {newForm.center_no && (() => {
                  const c = centers.find(x => x.center_no===newForm.center_no);
                  return c ? (
                    <div style={{ marginTop:14, padding:"12px 16px", background:"#0f172a", borderRadius:10, fontSize:13, color:"#94a3b8" }}>
                      <strong style={{ color:"#e2e8f0" }}>Center details:</strong> 📍 {c.center_name} · {c.location} &nbsp;|&nbsp; 👤 VM: {c.venue_manager} ({c.venue_manager_email}){c.assistant_venue_manager && <> &nbsp;|&nbsp; Asst: {c.assistant_venue_manager} ({c.assistant_venue_manager_email})</>}
                    </div>
                  ) : null;
                })()}
                <div style={{ marginTop:18, display:"flex", gap:10 }}>
                  <button type="submit" style={btn("#10b981")}>✓ Create &amp; Enter Expenses</button>
                  <button type="button" style={btn("#334155")} onClick={() => setShowNewForm(false)}>Cancel</button>
                </div>
              </form>
            </div>
          )}

          {loading ? <p style={{ color:"#94a3b8", textAlign:"center", padding:40 }}>Loading…</p> :
           sessions.length===0 ? (
            <div style={{ ...card, textAlign:"center", padding:60 }}>
              <div style={{ fontSize:48, marginBottom:12 }}>📋</div>
              <p style={{ color:"#94a3b8" }}>No OET exam sessions yet. Create one above.</p>
            </div>
          ) : (
            <div style={{ overflowX:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse", background:"#1e293b", borderRadius:16, overflow:"hidden" }}>
                <thead>
                  <tr style={{ background:"#0f172a" }}>
                    {["Exam Date","Center","Location","Venue Manager","Candidates","Support","Additional","Admin","Food","OET Total","Manager Exp.","Grand Total","🔍 Integrity","Actions"].map(h => (
                      <th key={h} style={{ padding:"12px 14px", textAlign:"left", fontSize:12, fontWeight:700, color:"#64748b", textTransform:"uppercase", letterSpacing:"0.05em", whiteSpace:"nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((s,i) => (
                    <tr key={s.id} style={{ borderTop:"1px solid #334155", background:i%2===0?"#1e293b":"#172033" }}>
                      <td style={{ padding:"12px 14px", fontWeight:600, whiteSpace:"nowrap" }}>{fmtDate(s.exam_date)}</td>
                      <td style={{ padding:"12px 14px" }}>
                        <span style={{ background:"#312e81", color:"#a5b4fc", borderRadius:6, padding:"3px 8px", fontSize:12, fontWeight:700 }}>{s.center_no}</span>
                        <span style={{ marginLeft:8, fontSize:13 }}>{s.center_name}</span>
                      </td>
                      <td style={{ padding:"12px 14px", color:"#94a3b8", fontSize:13 }}>{s.center_location}</td>
                      <td style={{ padding:"12px 14px", fontSize:13 }}>{s.venue_manager||"—"}</td>
                      <td style={{ padding:"12px 14px", textAlign:"center", fontWeight:600 }}>{s.candidate_count||0}</td>
                      <td style={{ padding:"12px 14px", color:"#3b82f6" }}>{fmt(s.support_cost)}</td>
                      <td style={{ padding:"12px 14px", color:"#f59e0b" }}>{fmt(s.additional_cost)}</td>
                      <td style={{ padding:"12px 14px", color:"#8b5cf6" }}>{fmt(s.admin_cost)}</td>
                      <td style={{ padding:"12px 14px", color:"#10b981" }}>{fmt(s.food_cost)}</td>
                      <td style={{ padding:"12px 14px", fontWeight:700 }}>{fmt(s.total_cost)}</td>
                      <td style={{ padding:"12px 14px", color:"#ec4899" }}>{fmt(s.manager_expense)}</td>
                      <td style={{ padding:"12px 14px", fontWeight:800, color:"#f59e0b" }}>{fmt(Number(s.total_cost||0)+Number(s.manager_expense||0))}</td>
                      <td style={{ padding:"12px 14px" }}>
                        <IntegrityBadge sessionId={s.id} integrityMap={integrityMap} />
                      </td>
                      <td style={{ padding:"12px 14px", whiteSpace:"nowrap" }}>
                        <button style={{ ...btn("#6366f1"), padding:"6px 12px", fontSize:12, marginRight:6 }} onClick={() => openSession(s)}>✏️ Enter</button>
                        <button style={{ ...btn("#ef4444"), padding:"6px 12px", fontSize:12 }} onClick={() => deleteSession(s.id)}>🗑</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ═══════════════════════ ENTRY TAB ═══════════════════════ */}
      {tab==="entry" && session && (
        <div>
          <div style={{ ...card, borderLeft:"4px solid #6366f1" }}>
            <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", flexWrap:"wrap", gap:12 }}>
              <div>
                <h2 style={{ margin:0, fontSize:20 }}>
                  {session.center_name}
                  <span style={{ background:"#312e81", color:"#a5b4fc", borderRadius:8, padding:"3px 10px", fontSize:13, fontWeight:700, marginLeft:10 }}>{session.center_no}</span>
                </h2>
                <p style={{ margin:"4px 0 0", color:"#94a3b8", fontSize:14 }}>📍 {session.center_location} &nbsp;·&nbsp; 📅 {fmtDate(session.exam_date)}</p>
                <p style={{ margin:"4px 0 0", fontSize:13, color:"#cbd5e1" }}>
                  👤 VM: <strong>{session.venue_manager||"—"}</strong>{session.venue_manager_email && <> ({session.venue_manager_email})</>}
                  {session.assistant_venue_manager && <> &nbsp;|&nbsp; Asst: <strong>{session.assistant_venue_manager}</strong>{session.assistant_venue_manager_email && <> ({session.assistant_venue_manager_email})</>}</>}
                </p>
              </div>
              <div style={{ display:"flex", gap:8 }}>
                <button style={btn("#10b981")} onClick={exportExcel}>⬇ Export Excel</button>
                <button style={btn("#334155")} onClick={() => { setTab("sessions"); loadSessions(); }}>← Back</button>
              </div>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(180px, 1fr))", gap:14, marginTop:18 }}>
              <div><span style={lbl}>Candidate Count</span><input type="number" min="0" style={inp} value={session.candidate_count||""} onChange={e => setSession(s=>({...s,candidate_count:e.target.value}))} onBlur={updateSession} /></div>
              <div><span style={lbl}>Invoice / Ref No.</span><input type="text" style={inp} value={session.invoice_ref||""} onChange={e => setSession(s=>({...s,invoice_ref:e.target.value}))} onBlur={updateSession} /></div>
              <div><span style={lbl}>Notes</span><input type="text" style={inp} value={session.notes||""} onChange={e => setSession(s=>({...s,notes:e.target.value}))} onBlur={updateSession} /></div>
            </div>
          </div>

          {/* Summary cards */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(155px, 1fr))", gap:14, marginBottom:24 }}>
            {Object.entries(GROUP_META).map(([k,g]) => (
              <div key={k} style={statCard(g.color)}>
                <div style={{ fontSize:20, marginBottom:4 }}>{g.icon}</div>
                <div style={{ fontSize:12, color:"#94a3b8", marginBottom:4 }}>{g.label}</div>
                <div style={{ fontSize:20, fontWeight:700, color:g.color }}>{fmt(groupTotal(k))}</div>
              </div>
            ))}
            <div style={statCard("#f1f5f9")}><div style={{ fontSize:20, marginBottom:4 }}>💰</div><div style={{ fontSize:12, color:"#94a3b8", marginBottom:4 }}>OET Total</div><div style={{ fontSize:20, fontWeight:700, color:"#f1f5f9" }}>{fmt(sessionTotal())}</div></div>
            {Number(session.candidate_count)>0 && <div style={statCard("#06b6d4")}><div style={{ fontSize:20, marginBottom:4 }}>👤</div><div style={{ fontSize:12, color:"#94a3b8", marginBottom:4 }}>Cost / Candidate</div><div style={{ fontSize:20, fontWeight:700, color:"#06b6d4" }}>{fmt(costPerCandidate())}</div></div>}
            {managerExp && managerExp.total>0 && <div style={statCard("#ec4899")}><div style={{ fontSize:20, marginBottom:4 }}>🧾</div><div style={{ fontSize:12, color:"#94a3b8", marginBottom:4 }}>Manager Expenses</div><div style={{ fontSize:20, fontWeight:700, color:"#ec4899" }}>{fmt(managerExp.total)}</div></div>}
            {managerExp && <div style={statCard("#f59e0b")}><div style={{ fontSize:20, marginBottom:4 }}>📊</div><div style={{ fontSize:12, color:"#94a3b8", marginBottom:4 }}>Grand Total</div><div style={{ fontSize:20, fontWeight:700, color:"#f59e0b" }}>{fmt(sessionTotal()+(managerExp?.total||0))}</div></div>}
          </div>

          {/* Expense Groups */}
          {Object.entries(groups).map(([groupKey, groupDef]) => {
            const gMeta = GROUP_META[groupKey]||{ label:groupDef.label, icon:"📌", color:"#64748b" };
            const groupExpenses = expenses.filter(e => e.expense_group===groupKey);
            return (
              <div key={groupKey} style={{ ...card, marginBottom:20 }}>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
                  <h3 style={{ margin:0, fontSize:17, display:"flex", alignItems:"center", gap:8 }}>
                    <span style={{ fontSize:22 }}>{gMeta.icon}</span>
                    <span style={{ color:gMeta.color }}>{gMeta.label}</span>
                    <span style={{ fontSize:13, color:"#94a3b8", fontWeight:400 }}>({groupExpenses.length} entries)</span>
                  </h3>
                  <span style={{ fontWeight:700, color:gMeta.color, fontSize:16 }}>{fmt(groupTotal(groupKey))}</span>
                </div>
                {groupDef.items.map(item => {
                  const rowsForType = groupExpenses.filter(e => e.expense_type===item.key);
                  return (
                    <div key={item.key} style={{ marginBottom:14 }}>
                      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8 }}>
                        <span style={{ fontSize:14, fontWeight:600, color:"#e2e8f0" }}>{item.label}</span>
                        <button style={{ background:"transparent", color:gMeta.color, border:`1px solid ${gMeta.color}`, borderRadius:8, padding:"4px 10px", fontSize:12, cursor:"pointer", fontWeight:600 }}
                          onClick={() => addExpenseRow(groupKey, item.key, item.label)}>+ Add Row</button>
                      </div>
                      {rowsForType.length===0 ? (
                        <p style={{ color:"#475569", fontSize:12, margin:"0 0 8px", fontStyle:"italic" }}>No entries yet — click "+ Add Row"</p>
                      ) : rowsForType.map(exp => (
                        <ExpenseRow key={exp.id} exp={exp} groupKey={groupKey} typeKey={exp.expense_type}
                          autoFill={{ adminCandidateCount, totalSupportStaff, invigilatorCount }}
                          onUpdate={updateExpense} onSave={saveExpense} onDelete={deleteExpense} color={gMeta.color} />
                      ))}
                    </div>
                  );
                })}
              </div>
            );
          })}

          {/* Manager Expenses */}
          <div style={{ ...card, borderLeft:"4px solid #ec4899" }}>
            <h3 style={{ margin:"0 0 16px", fontSize:17, color:"#ec4899", display:"flex", alignItems:"center", gap:8 }}>
              🧾 Manager Expenses
              <span style={{ fontSize:12, fontWeight:400, color:"#94a3b8" }}>— Travel & field expenses by VM & Asst. VM for this exam</span>
            </h3>
            {!managerExp ? (
              <p style={{ color:"#475569", fontSize:13, fontStyle:"italic" }}>Loading manager expenses…</p>
            ) : managerExp.by_person?.length===0 ? (
              <p style={{ color:"#475569", fontSize:13, fontStyle:"italic" }}>No employee expenses found for this center + exam date. Pulled automatically when VM/Asst. VM submits their trip expenses.</p>
            ) : (
              <>
                <div style={{ display:"flex", gap:12, flexWrap:"wrap", marginBottom:20 }}>
                  {managerExp.by_person.map(p => (
                    <div key={p.employee_email} style={{ background:"#0f172a", borderRadius:12, padding:"14px 18px", borderLeft:`3px solid ${ROLE_COLOR[p.role_label]||"#64748b"}` }}>
                      <div style={{ fontSize:12, color:ROLE_COLOR[p.role_label]||"#64748b", fontWeight:700, marginBottom:4 }}>{p.role_label}</div>
                      <div style={{ fontSize:14, fontWeight:700, color:"#f1f5f9" }}>{p.employee_name}</div>
                      <div style={{ fontSize:11, color:"#64748b", marginBottom:8 }}>{p.employee_email}</div>
                      <div style={{ fontSize:18, fontWeight:800, color:ROLE_COLOR[p.role_label]||"#f1f5f9" }}>{fmt(p.total_amount)}</div>
                      <div style={{ fontSize:10, color:"#64748b" }}>{p.trip_count} trip(s)</div>
                    </div>
                  ))}
                  <div style={{ background:"#0f172a", borderRadius:12, padding:"14px 18px", borderLeft:"3px solid #f1f5f9", display:"flex", flexDirection:"column", justifyContent:"center" }}>
                    <div style={{ fontSize:12, color:"#94a3b8", fontWeight:700, marginBottom:4 }}>COMBINED</div>
                    <div style={{ fontSize:20, fontWeight:800, color:"#f1f5f9" }}>{fmt(managerExp.total)}</div>
                  </div>
                </div>
                <div style={{ overflowX:"auto" }}>
                  <table style={{ width:"100%", borderCollapse:"collapse" }}>
                    <thead><tr style={{ borderBottom:"1px solid #334155" }}>
                      {["Person","Role","Category","Entries","Amount"].map(h => <th key={h} style={{ padding:"10px 12px", textAlign:"left", fontSize:11, color:"#64748b", fontWeight:700, textTransform:"uppercase" }}>{h}</th>)}
                    </tr></thead>
                    <tbody>
                      {managerExp.by_category.map((r,i) => (
                        <tr key={i} style={{ borderBottom:"1px solid #1e293b" }}>
                          <td style={{ padding:"10px 12px", fontSize:13, fontWeight:600 }}>{r.employee_name}</td>
                          <td style={{ padding:"10px 12px" }}><span style={{ background:(ROLE_COLOR[r.role_label]||"#64748b")+"22", color:ROLE_COLOR[r.role_label]||"#64748b", borderRadius:6, padding:"2px 8px", fontSize:11, fontWeight:700 }}>{r.role_label}</span></td>
                          <td style={{ padding:"10px 12px", fontSize:13, color:CATEGORY_COLORS[r.category]||"#94a3b8" }}>{r.category}</td>
                          <td style={{ padding:"10px 12px", textAlign:"right", color:"#94a3b8", fontSize:13 }}>{r.entry_count}</td>
                          <td style={{ padding:"10px 12px", textAlign:"right", fontWeight:700, color:"#f59e0b" }}>{fmt(r.total_amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot><tr style={{ borderTop:"2px solid #334155" }}>
                      <td colSpan={4} style={{ padding:"10px 12px", fontWeight:700, fontSize:13 }}>Grand Total</td>
                      <td style={{ padding:"10px 12px", textAlign:"right", fontWeight:800, color:"#ec4899", fontSize:15 }}>{fmt(managerExp.total)}</td>
                    </tr></tfoot>
                  </table>
                </div>
              </>
            )}
          </div>

          {/* ── Cross-Check: Declared vs Billed ─────────────────────────────── */}
          <CrossCheckPanel crossCheck={crossCheck} />
        </div>
      )}

      {/* ═══════════════════════ REPORTS TAB ═══════════════════════ */}
      {tab==="company" && (
        <div>
          <div style={card}>
            <h3 style={{marginTop:0}}>Add Company-Paid Hotel or Travel Cost</h3>
            <form onSubmit={saveCompanyCost}>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(190px,1fr))",gap:14}}>
                <div><span style={lbl}>Employee *</span><select required style={inp} value={companyForm.employee_id} onChange={e=>setCompanyForm(f=>({...f,employee_id:e.target.value,trip_id:""}))}><option value="">Select employee</option>{[...new Map(companyOptions.map(o=>[o.employee_id,o])).values()].map(o=><option key={o.employee_id} value={o.employee_id}>{o.employee_name} — {o.email}</option>)}</select></div>
                <div><span style={lbl}>Trip *</span><select required style={inp} value={companyForm.trip_id} onChange={e=>setCompanyForm(f=>({...f,trip_id:e.target.value}))}><option value="">Select trip</option>{companyOptions.filter(o=>String(o.employee_id)===String(companyForm.employee_id)).map(o=><option key={o.trip_id} value={o.trip_id}>{o.trip_title} ({fmtDate(o.start_date)})</option>)}</select></div>
                <div><span style={lbl}>Cost Type *</span><select style={inp} value={companyForm.cost_type} onChange={e=>setCompanyForm(f=>({...f,cost_type:e.target.value}))}>{["Hotel","Train","Bus","Flight"].map(x=><option key={x}>{x}</option>)}</select></div>
                <div><span style={lbl}>Date *</span><input required type="date" style={inp} value={companyForm.expense_date} onChange={e=>setCompanyForm(f=>({...f,expense_date:e.target.value}))}/></div>
                <div><span style={lbl}>Amount *</span><input required type="number" min="0.01" step="0.01" style={inp} value={companyForm.amount} onChange={e=>setCompanyForm(f=>({...f,amount:e.target.value}))}/></div>
                {companyForm.cost_type==="Hotel" ? <><div><span style={lbl}>Hotel Name *</span><input required style={inp} value={companyForm.hotel_name} onChange={e=>setCompanyForm(f=>({...f,hotel_name:e.target.value}))}/></div><div><span style={lbl}>Hotel Location *</span><input required style={inp} value={companyForm.hotel_location} onChange={e=>setCompanyForm(f=>({...f,hotel_location:e.target.value}))}/></div></> : <><div><span style={lbl}>From *</span><input required style={inp} value={companyForm.travel_from} onChange={e=>setCompanyForm(f=>({...f,travel_from:e.target.value}))}/></div><div><span style={lbl}>To *</span><input required style={inp} value={companyForm.travel_to} onChange={e=>setCompanyForm(f=>({...f,travel_to:e.target.value}))}/></div></>}
                <div><span style={lbl}>Booking Reference</span><input style={inp} value={companyForm.booking_reference} onChange={e=>setCompanyForm(f=>({...f,booking_reference:e.target.value}))}/></div>
              </div>
              <button type="submit" style={{...btn("#10b981"),marginTop:16}}>Add Company-Paid Cost</button>
            </form>
          </div>
          <div style={card}><h3>Company-Paid Cost Register</h3><div style={{overflowX:"auto"}}><table style={{width:"100%",borderCollapse:"collapse"}}><thead><tr>{["Employee","Trip","Type","Date","Amount","Route / Hotel"].map(h=><th key={h} style={{textAlign:"left",padding:10,color:"#94a3b8"}}>{h}</th>)}</tr></thead><tbody>{companyCosts.map(c=><tr key={c.id} style={{borderTop:"1px solid #334155"}}><td style={{padding:10}}>{c.employee_name}</td><td>{c.trip_title}</td><td>{c.category==="Hotel"?"Hotel":c.travel_subcategory}</td><td>{fmtDate(c.expense_date)}</td><td>{fmt(c.amount)}</td><td>{c.category==="Hotel"?`${c.hotel_name||""}, ${c.hotel_location||""}`:`${c.travel_from||""} → ${c.travel_to||""}`}</td></tr>)}</tbody></table></div><div style={{marginTop:14,fontWeight:700}}>Company-paid total: {fmt(companyCosts.reduce((s,c)=>s+Number(c.amount||0),0))}</div></div>
        </div>
      )}

      {tab==="reports" && (
        <div>
          <div style={{ ...card, marginBottom:20 }}>
            <h3 style={{ margin:"0 0 14px", fontSize:16 }}>📊 Filter Reports</h3>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(200px, 1fr))", gap:14, alignItems:"flex-end" }}>
              <div>
                <span style={lbl}>OET Exam Date</span>
                <select style={inp} value={rptFilter.exam_date} onChange={e => setRptFilter(f=>({...f,exam_date:e.target.value}))}>
                  <option value="">All Exam Dates</option>
                  {oetTestDates.map(d => <option key={d} value={d}>{fmtDate(d)}</option>)}
                </select>
              </div>
              <div>
                <span style={lbl}>Center</span>
                <select style={inp} value={rptFilter.center_no} onChange={e => setRptFilter(f=>({...f,center_no:e.target.value}))}>
                  <option value="">All Centers</option>
                  {centers.map(c => <option key={c.center_no} value={c.center_no}>{c.center_no} — {c.center_name}</option>)}
                </select>
              </div>
              <div><button style={btn("#6366f1")} onClick={loadReports}>🔍 Generate Report</button></div>
            </div>
            {(rptFilter.exam_date||rptFilter.center_no) && (
              <div style={{ marginTop:10, fontSize:13, color:"#94a3b8" }}>
                Showing: {rptFilter.exam_date ? <strong style={{ color:"#a5b4fc" }}>{fmtDate(rptFilter.exam_date)}</strong> : "All dates"}
                {rptFilter.center_no && <> · {centers.find(c=>c.center_no===rptFilter.center_no)?.center_name}</>}
              </div>
            )}
          </div>

          {loading && <p style={{ color:"#94a3b8", textAlign:"center", padding:40 }}>Loading reports…</p>}

          {reports && (
            <>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(155px, 1fr))", gap:14, marginBottom:24 }}>
                {[
                  { label:"Total Sessions",   value:reports.summary.total_sessions,            icon:"📋", color:"#6366f1" },
                  { label:"Centers Active",   value:reports.summary.total_centers,             icon:"🏫", color:"#3b82f6" },
                  { label:"Candidates",       value:reports.summary.total_candidates||0,       icon:"👥", color:"#10b981" },
                  { label:"OET Exam Cost",    value:fmt(reports.summary.grand_total),          icon:"💰", color:"#f59e0b" },
                  { label:"Manager Expenses", value:fmt(reports.summary.total_manager_expense),icon:"🧾", color:"#ec4899" },
                  { label:"Grand Total",      value:fmt(reports.summary.combined_total),       icon:"📊", color:"#f1f5f9" },
                  { label:"Avg/Candidate",    value:fmt(reports.summary.avg_cost_per_candidate),icon:"👤",color:"#06b6d4" },
                ].map(s => (
                  <div key={s.label} style={statCard(s.color)}>
                    <div style={{ fontSize:22, marginBottom:4 }}>{s.icon}</div>
                    <div style={{ fontSize:11, color:"#94a3b8", textTransform:"uppercase", letterSpacing:"0.05em" }}>{s.label}</div>
                    <div style={{ fontSize:18, fontWeight:700, color:s.color, marginTop:4 }}>{s.value}</div>
                  </div>
                ))}
              </div>

              <div style={{ ...card, marginBottom:20 }}>
                <h3 style={{ margin:"0 0 14px", fontSize:16 }}>🏫 Cost per Center (with Manager Expenses)</h3>
                <div style={{ overflowX:"auto" }}>
                  <table style={{ width:"100%", borderCollapse:"collapse" }}>
                    <thead><tr style={{ borderBottom:"1px solid #334155" }}>
                      {["Center","Location","Exams","Candidates","Support","Additional","Admin","Food","OET Total","VM Exp","Asst VM","Mgr Total","Grand Total","Per Candidate"].map(h => (
                        <th key={h} style={{ padding:"10px 12px", textAlign:"left", fontSize:11, color:"#64748b", fontWeight:700, textTransform:"uppercase", whiteSpace:"nowrap" }}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {reports.by_center.map(r => (
                        <tr key={r.center_no} style={{ borderBottom:"1px solid #1e293b" }}>
                          <td style={{ padding:"10px 12px" }}><span style={{ background:"#312e81", color:"#a5b4fc", borderRadius:6, padding:"2px 8px", fontSize:11, fontWeight:700 }}>{r.center_no}</span><div style={{ fontWeight:600, fontSize:12, marginTop:2 }}>{r.center_name}</div></td>
                          <td style={{ padding:"10px 12px", color:"#94a3b8", fontSize:12 }}>{r.center_location}</td>
                          <td style={{ padding:"10px 12px", textAlign:"center" }}>{r.exam_count}</td>
                          <td style={{ padding:"10px 12px", textAlign:"center" }}>{r.total_candidates||0}</td>
                          <td style={{ padding:"10px 12px", color:"#3b82f6" }}>{fmt(r.support_cost)}</td>
                          <td style={{ padding:"10px 12px", color:"#f59e0b" }}>{fmt(r.additional_cost)}</td>
                          <td style={{ padding:"10px 12px", color:"#8b5cf6" }}>{fmt(r.admin_cost)}</td>
                          <td style={{ padding:"10px 12px", color:"#10b981" }}>{fmt(r.food_cost)}</td>
                          <td style={{ padding:"10px 12px", fontWeight:700 }}>{fmt(r.total_oet_cost)}</td>
                          <td style={{ padding:"10px 12px", color:"#6366f1" }}>{fmt(r.vm_expense)}</td>
                          <td style={{ padding:"10px 12px", color:"#ec4899" }}>{fmt(r.asst_expense)}</td>
                          <td style={{ padding:"10px 12px", color:"#ec4899", fontWeight:600 }}>{fmt(r.manager_expense)}</td>
                          <td style={{ padding:"10px 12px", fontWeight:800, color:"#f59e0b" }}>{fmt(r.grand_total)}</td>
                          <td style={{ padding:"10px 12px", color:"#06b6d4" }}>{fmt(r.cost_per_candidate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div style={{ ...card, marginBottom:20 }}>
                <h3 style={{ margin:"0 0 14px", fontSize:16 }}>📅 Cost per Exam Date</h3>
                <div style={{ overflowX:"auto" }}>
                  <table style={{ width:"100%", borderCollapse:"collapse" }}>
                    <thead><tr style={{ borderBottom:"1px solid #334155" }}>
                      {["Date","Centers","Candidates","Support","Additional","Admin","Food","Total"].map(h => (
                        <th key={h} style={{ padding:"10px 12px", textAlign:"left", fontSize:11, color:"#64748b", fontWeight:700, textTransform:"uppercase", whiteSpace:"nowrap" }}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {reports.by_date.map(r => (
                        <tr key={r.exam_date} style={{ borderBottom:"1px solid #1e293b" }}>
                          <td style={{ padding:"10px 12px", fontWeight:600, whiteSpace:"nowrap" }}>{fmtDate(r.exam_date)}</td>
                          <td style={{ padding:"10px 12px", textAlign:"center" }}>{r.center_count}</td>
                          <td style={{ padding:"10px 12px", textAlign:"center" }}>{r.total_candidates||0}</td>
                          <td style={{ padding:"10px 12px", color:"#3b82f6" }}>{fmt(r.support_cost)}</td>
                          <td style={{ padding:"10px 12px", color:"#f59e0b" }}>{fmt(r.additional_cost)}</td>
                          <td style={{ padding:"10px 12px", color:"#8b5cf6" }}>{fmt(r.admin_cost)}</td>
                          <td style={{ padding:"10px 12px", color:"#10b981" }}>{fmt(r.food_cost)}</td>
                          <td style={{ padding:"10px 12px", fontWeight:700 }}>{fmt(r.total_cost)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {reports.manager_expenses?.length>0 && (
                <div style={{ ...card, borderLeft:"4px solid #ec4899", marginBottom:20 }}>
                  <h3 style={{ margin:"0 0 14px", fontSize:16, color:"#ec4899" }}>🧾 Manager Expense Breakdown</h3>
                  <div style={{ overflowX:"auto" }}>
                    <table style={{ width:"100%", borderCollapse:"collapse" }}>
                      <thead><tr style={{ borderBottom:"1px solid #334155" }}>
                        {["Name","Role","Center","Trips","Total Spent"].map(h => <th key={h} style={{ padding:"10px 12px", textAlign:"left", fontSize:11, color:"#64748b", fontWeight:700, textTransform:"uppercase" }}>{h}</th>)}
                      </tr></thead>
                      <tbody>
                        {reports.manager_expenses.map((r,i) => (
                          <tr key={i} style={{ borderBottom:"1px solid #1e293b" }}>
                            <td style={{ padding:"10px 12px", fontWeight:600 }}>{r.employee_name}</td>
                            <td style={{ padding:"10px 12px" }}><span style={{ background:(ROLE_COLOR[r.role_label]||"#64748b")+"22", color:ROLE_COLOR[r.role_label]||"#64748b", borderRadius:6, padding:"2px 8px", fontSize:11, fontWeight:700 }}>{r.role_label}</span></td>
                            <td style={{ padding:"10px 12px", fontSize:13 }}>{r.center_name} <span style={{ color:"#64748b" }}>({r.center_no})</span></td>
                            <td style={{ padding:"10px 12px", textAlign:"center" }}>{r.trip_count}</td>
                            <td style={{ padding:"10px 12px", fontWeight:700, color:"#f59e0b" }}>{fmt(r.total_amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot><tr style={{ borderTop:"2px solid #334155" }}>
                        <td colSpan={4} style={{ padding:"10px 12px", fontWeight:700 }}>Total Manager Expenses</td>
                        <td style={{ padding:"10px 12px", fontWeight:800, color:"#ec4899", fontSize:15 }}>{fmt(reports.manager_expenses.reduce((s,r)=>s+Number(r.total_amount),0))}</td>
                      </tr></tfoot>
                    </table>
                  </div>
                </div>
              )}

              <div style={card}>
                <h3 style={{ margin:"0 0 14px", fontSize:16 }}>🔍 Cost by Expense Type</h3>
                <div style={{ overflowX:"auto" }}>
                  <table style={{ width:"100%", borderCollapse:"collapse" }}>
                    <thead><tr style={{ borderBottom:"1px solid #334155" }}>
                      {["Group","Type","Occurrences","Total Amount","Avg/Session"].map(h => <th key={h} style={{ padding:"10px 12px", textAlign:"left", fontSize:11, color:"#64748b", fontWeight:700, textTransform:"uppercase", whiteSpace:"nowrap" }}>{h}</th>)}
                    </tr></thead>
                    <tbody>
                      {reports.by_group.map((r,i) => {
                        const gm = GROUP_META[r.expense_group]||{ label:r.expense_group, color:"#64748b" };
                        return (
                          <tr key={i} style={{ borderBottom:"1px solid #1e293b" }}>
                            <td style={{ padding:"10px 12px" }}><span style={{ color:gm.color, fontSize:12, fontWeight:700, background:gm.color+"22", borderRadius:6, padding:"2px 8px" }}>{gm.label}</span></td>
                            <td style={{ padding:"10px 12px", color:"#e2e8f0" }}>{r.expense_label||r.expense_type}</td>
                            <td style={{ padding:"10px 12px", textAlign:"center" }}>{r.occurrence_count}</td>
                            <td style={{ padding:"10px 12px", fontWeight:700, color:"#f59e0b" }}>{fmt(r.total_amount)}</td>
                            <td style={{ padding:"10px 12px", color:"#94a3b8" }}>{fmt(r.avg_amount)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── IntegrityBadge — small status pill shown in the sessions table ────────────
function IntegrityBadge({ sessionId, integrityMap }) {
  const severity = integrityMap?.[sessionId];
  if (!severity || severity === "ok") {
    return <span style={{ fontSize:11, fontWeight:700, padding:"3px 9px", borderRadius:6,
                          background:"#052e16", color:"#4ade80", border:"1px solid #166534" }}>✅ OK</span>;
  }
  if (severity === "warning") {
    return <span style={{ fontSize:11, fontWeight:700, padding:"3px 9px", borderRadius:6,
                          background:"#422006", color:"#fbbf24", border:"1px solid #92400e" }}>⚠️ Review</span>;
  }
  return <span style={{ fontSize:11, fontWeight:700, padding:"3px 9px", borderRadius:6,
                        background:"#450a0a", color:"#f87171", border:"1px solid #991b1b" }}>🚨 Mismatch</span>;
}

// ── CrossCheckPanel — declared vs billed reconciliation ───────────────────────
function CrossCheckPanel({ crossCheck }) {
  if (!crossCheck) return null;
  if (!crossCheck.has_declarations) {
    return (
      <div style={{ background:"#1e293b", borderRadius:16, padding:"18px 22px", marginBottom:20, border:"1px solid #334155" }}>
        <div style={{ fontSize:14, color:"#64748b" }}>
          ℹ️ No staff/candidate declaration found for this session. The Venue Manager or Asst. Venue Manager must declare counts during their advance request.
        </div>
      </div>
    );
  }

  const severityStyle = {
    ok:       { bg:"#052e16", border:"#166534", badge:"#22c55e",  icon:"✅" },
    warning:  { bg:"#422006", border:"#92400e", badge:"#f59e0b",  icon:"⚠️" },
    critical: { bg:"#450a0a", border:"#991b1b", badge:"#ef4444",  icon:"🚨" },
  };
  const overall = severityStyle[crossCheck.overall_severity] || severityStyle.ok;

  return (
    <div style={{ background:"#1e293b", borderRadius:16, padding:"20px 24px", marginBottom:20,
                  border:`2px solid ${overall.border}` }}>
      {/* Header */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <span style={{ fontSize:20 }}>{overall.icon}</span>
          <div>
            <div style={{ fontSize:15, fontWeight:700, color:"#f1f5f9" }}>Staff & Candidate Cross-Check</div>
            <div style={{ fontSize:12, color:"#94a3b8", marginTop:2 }}>
              Declared by venue manager vs billed in OET expenses
            </div>
          </div>
        </div>
        <span style={{ background:overall.badge+"33", color:overall.badge, border:`1px solid ${overall.badge}`,
                       borderRadius:8, padding:"4px 12px", fontSize:12, fontWeight:700, textTransform:"uppercase" }}>
          {crossCheck.overall_severity}
        </span>
      </div>

      {/* Flag rows */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(240px, 1fr))", gap:12, marginBottom:16 }}>
        {crossCheck.flags.map(flag => {
          const s = severityStyle[flag.severity] || severityStyle.ok;
          return (
            <div key={flag.category} style={{ background:s.bg, border:`1px solid ${s.border}`,
                                              borderRadius:12, padding:"12px 16px" }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                <span style={{ fontSize:13, fontWeight:700, color:"#e2e8f0" }}>{flag.category}</span>
                <span style={{ fontSize:16 }}>{s.icon}</span>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:6, marginBottom:8 }}>
                <div style={{ background:"#0f172a", borderRadius:8, padding:"6px 10px", textAlign:"center" }}>
                  <div style={{ fontSize:10, color:"#64748b", marginBottom:2, textTransform:"uppercase" }}>Declared</div>
                  <div style={{ fontSize:20, fontWeight:800, color:"#60a5fa" }}>{flag.declared}</div>
                </div>
                <div style={{ background:"#0f172a", borderRadius:8, padding:"6px 10px", textAlign:"center" }}>
                  <div style={{ fontSize:10, color:"#64748b", marginBottom:2, textTransform:"uppercase" }}>Billed</div>
                  <div style={{ fontSize:20, fontWeight:800,
                                color: flag.severity === "ok" ? "#4ade80" : flag.severity === "warning" ? "#f59e0b" : "#f87171" }}>
                    {flag.billed}
                  </div>
                </div>
              </div>
              {flag.variance !== 0 && (
                <div style={{ fontSize:12, color: flag.variance > 0 ? "#fca5a5" : "#86efac",
                              background:"#0f172a", borderRadius:6, padding:"4px 8px", marginBottom:6, fontWeight:600 }}>
                  {flag.variance > 0 ? `▲ Over-billed by ${flag.variance}` : `▼ Under-billed by ${Math.abs(flag.variance)}`}
                  {flag.pct_variance > 0 && ` (${flag.pct_variance}%)`}
                </div>
              )}
              <div style={{ fontSize:12, color:s.badge }}>{flag.note}</div>
              {flag.names_declared && (
                <div style={{ marginTop:8, fontSize:11, color:"#94a3b8", borderTop:"1px solid #1e293b", paddingTop:6 }}>
                  <span style={{ fontWeight:600, color:"#64748b" }}>Declared names: </span>{flag.names_declared}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Raw declarations accordion */}
      {crossCheck.declarations?.length > 0 && (
        <details style={{ marginTop:4 }}>
          <summary style={{ fontSize:12, color:"#64748b", cursor:"pointer", userSelect:"none" }}>
            View raw declarations ({crossCheck.declarations.length} submission{crossCheck.declarations.length > 1 ? "s" : ""})
          </summary>
          <div style={{ marginTop:10, display:"flex", flexDirection:"column", gap:8 }}>
            {crossCheck.declarations.map((d, i) => (
              <div key={i} style={{ background:"#0f172a", borderRadius:10, padding:"10px 14px", fontSize:12 }}>
                <div style={{ fontWeight:700, color:"#e2e8f0", marginBottom:4 }}>{d.employee_name}</div>
                <div style={{ display:"flex", flexWrap:"wrap", gap:12, color:"#94a3b8" }}>
                  <span>👥 Candidates: <strong style={{ color:"#60a5fa" }}>{d.oet_candidate_count}</strong></span>
                  <span>🎤 Interlocutors: <strong style={{ color:"#a78bfa" }}>{d.oet_interlocutor_count}</strong>
                    {d.oet_interlocutor_names && <span style={{ color:"#6d7fa5" }}> ({d.oet_interlocutor_names})</span>}
                  </span>
                  <span>🔧 Freelancers: <strong style={{ color:"#fb923c" }}>{d.oet_freelancer_count}</strong>
                    {d.oet_freelancer_names && <span style={{ color:"#6d7fa5" }}> ({d.oet_freelancer_names})</span>}
                  </span>
                  <span>👷 Support Staff: <strong style={{ color:"#34d399" }}>{d.oet_support_staff_count}</strong>
                    {d.oet_support_staff_names && <span style={{ color:"#6d7fa5" }}> ({d.oet_support_staff_names})</span>}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

// ── ExpenseRow — smart field rendering based on group + type ──────────────────
function ExpenseRow({ exp, groupKey, typeKey, autoFill, onUpdate, onSave, onDelete, color }) {
  const i2 = { padding:"8px 10px", borderRadius:8, border:"1px solid #334155", background:"#0f172a", color:"#f1f5f9", fontSize:13, width:"100%", boxSizing:"border-box", outline:"none" };
  const fl  = { fontSize:10, color:"#64748b", fontWeight:700, marginBottom:3, textTransform:"uppercase" };
  const ro  = { ...i2, background:"#0a0f1a", color:"#64748b", cursor:"not-allowed" };

  // Determine which extra fields to show
  const isSupport = groupKey === "support_services";
  const isFood    = groupKey === "food_expenses";
  const isAdmin   = groupKey === "administrative_charges";

  // For administrative_charges — show quantity field with appropriate label
  const adminQtyLabel = { per_candidate:"No. of Candidates", consolidated_amount:"Total No. of Candidates", room_wise_cost:"Total No. of Rooms" }[typeKey];

  // For food — auto-fill staff_count from computed values, allow override
  const foodAutoCount = { candidates_food: autoFill?.adminCandidateCount, venue_staff_food: autoFill?.totalSupportStaff, interlocutors_food: autoFill?.invigilatorCount }[typeKey];
  const foodCountLabel = { candidates_food:"No. of Candidates", venue_staff_food:"Venue Staff Count", interlocutors_food:"No. of Interlocutors", ebek_staff_food:"No. of Ebek Staff" }[typeKey];

  return (
    <div style={{ background:"#0f172a", borderRadius:12, padding:"12px 14px", marginBottom:8, borderLeft:`3px solid ${color}` }}>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(110px, 1fr))", gap:8, alignItems:"center" }}>
        {/* Amount */}
        <div>
          <div style={fl}>Amount (₹)</div>
          <input type="number" min="0" step="0.01" style={{ ...i2, fontWeight:700, color:"#f59e0b" }}
            value={exp.amount||""} onChange={e => onUpdate(exp.id,"amount",e.target.value)} onBlur={() => onSave(exp)} />
        </div>

        {/* Support Services: No. of Staff */}
        {isSupport && (
          <div>
            <div style={fl}>No. of Staff</div>
            <input type="number" min="0" style={{ ...i2, color:"#a5b4fc" }}
              value={exp.staff_count||""} onChange={e => onUpdate(exp.id,"staff_count",e.target.value)} onBlur={() => onSave(exp)} />
          </div>
        )}

        {/* Administrative Charges: quantity field */}
        {isAdmin && adminQtyLabel && (
          <div>
            <div style={fl}>{adminQtyLabel}</div>
            <input type="number" min="0" style={{ ...i2, color:"#c4b5fd" }}
              value={exp.quantity||""} onChange={e => onUpdate(exp.id,"quantity",e.target.value)} onBlur={() => onSave(exp)} />
          </div>
        )}

        {/* Food: person count (auto-fill or manual) */}
        {isFood && (
          <div>
            <div style={fl}>
              {foodCountLabel}
              {foodAutoCount !== undefined && foodAutoCount > 0 && <span style={{ color:"#10b981", marginLeft:4 }}>●auto</span>}
            </div>
            {foodAutoCount !== undefined && foodAutoCount > 0 ? (
              <div style={{ position:"relative" }}>
                <input type="number" min="0" style={{ ...i2, color:"#86efac", borderColor:"#166534" }}
                  value={exp.staff_count !== null && exp.staff_count !== undefined && exp.staff_count !== "" ? exp.staff_count : foodAutoCount}
                  onChange={e => onUpdate(exp.id,"staff_count",e.target.value)} onBlur={() => onSave(exp)}
                  placeholder={String(foodAutoCount)} />
                {(exp.staff_count===null||exp.staff_count===undefined||exp.staff_count==="") && (
                  <div style={{ position:"absolute", right:8, top:"50%", transform:"translateY(-50%)", fontSize:10, color:"#4ade80", pointerEvents:"none" }}>auto</div>
                )}
              </div>
            ) : (
              <input type="number" min="0" style={{ ...i2, color:"#86efac" }}
                value={exp.staff_count||""} onChange={e => onUpdate(exp.id,"staff_count",e.target.value)} onBlur={() => onSave(exp)} />
            )}
          </div>
        )}

        {/* Food: Food Qty Ordered */}
        {isFood && (
          <div>
            <div style={fl}>Food Qty Ordered</div>
            <input type="number" min="0" style={{ ...i2, color:"#fde68a" }}
              value={exp.quantity||""} onChange={e => onUpdate(exp.id,"quantity",e.target.value)} onBlur={() => onSave(exp)} />
          </div>
        )}

        {/* Vendor */}
        <div>
          <div style={fl}>Vendor / Paid To</div>
          <input type="text" style={i2} placeholder="Name / company"
            value={exp.vendor_paid_to||""} onChange={e => onUpdate(exp.id,"vendor_paid_to",e.target.value)} onBlur={() => onSave(exp)} />
        </div>

        {/* Payment Mode */}
        <div>
          <div style={fl}>Payment Mode</div>
          <select style={i2} value={exp.payment_mode||""}
            onChange={e => { onUpdate(exp.id,"payment_mode",e.target.value); onSave({...exp,payment_mode:e.target.value}); }}>
            <option value="">Select…</option>
            {["Cash","UPI","Bank Transfer","Card","Cheque"].map(m => <option key={m}>{m}</option>)}
          </select>
        </div>

        {/* Invoice */}
        <div>
          <div style={fl}>Invoice / Bill No.</div>
          <input type="text" style={i2} placeholder="INV-001"
            value={exp.invoice_number||""} onChange={e => onUpdate(exp.id,"invoice_number",e.target.value)} onBlur={() => onSave(exp)} />
        </div>

        {/* Paid Date */}
        <div>
          <div style={fl}>Paid Date</div>
          <input type="date" style={i2}
            value={exp.paid_date ? exp.paid_date.split("T")[0] : ""}
            onChange={e => { onUpdate(exp.id,"paid_date",e.target.value); onSave({...exp,paid_date:e.target.value}); }} />
        </div>

        {/* Remarks */}
        <div>
          <div style={fl}>Remarks</div>
          <input type="text" style={i2} placeholder="Optional"
            value={exp.remarks||""} onChange={e => onUpdate(exp.id,"remarks",e.target.value)} onBlur={() => onSave(exp)} />
        </div>
      </div>
      <div style={{ textAlign:"right", marginTop:6 }}>
        <button style={{ background:"transparent", color:"#ef4444", border:"none", cursor:"pointer", fontSize:12 }}
          onClick={() => { if (window.confirm("Remove this entry?")) onDelete(exp.id); }}>🗑 Remove</button>
      </div>
    </div>
  );
}
